import { defineStore } from 'pinia'

import { useUsersStore } from '~/entities/user/user/stores/users'

import type { ReceivedInvitation } from '~/features-swym/network-activity/interfaces/network-activity'
import { EInvitationAcceptStatus, EInvitationsMode } from '~/features-swym/network-activity/interfaces/network-activity'
import { useNetworkStore } from '~/features-swym/network-activity/stores/network-activities'
import {
  acceptInvitation as accept,
  declineInvitation as decline,
  getInvitations,
} from '~/features-swym/network-invitation/api/network-invitations'

import type { SwymHTTPError } from '~/typings/SwymHTTPError'

// eslint-disable-next-line max-statements
export const useInvitationsStore = defineStore('network-invitations', () => {
  // #region DEPENDENCIES
  const { currentUser } = useUsersStore()
  const { threads, addNewThread } = useNetworkStore()
  // #endregion

  // #region STATE
  /**
   * Array of all the received invitations
   */
  const received = reactive<ReceivedInvitation[]>([])

  /**
   * Array of all the sent invitations
   */
  // const sent = reactive<ReceivedInvitation[]>([])

  /**
   * The current mode to display onvitations (received/sent)
   */
  const currentMode = ref<EInvitationsMode>(EInvitationsMode.Received)

  /**
   * true if the invites badge should be shown in navigation bar/thread tile
   */
  const showInvitesBadge = ref(true)

  /**
   * true if the invitations have been fetched yet
   */
  const fetchedInvites = ref(false)
  // #endregion

  // #region COMPUTED
  /**
   * The computed for the invites
   */
  const contents = computed<ReceivedInvitation[]>(() => {
    if (currentMode.value === EInvitationsMode.Received)
      return received
    return []
  })

  /**
   * The count of received invites
   */
  const countInvites = computed(() => {
    return received.length
  })

  /**
   * true if we have any received invites
   */
  const hasInvites = computedEager(() => {
    return received.length > 0
  })
  // #endregion

  // #region GETTERS
  /**
   * Getter for invitation items
   */
  const getContents = () => {
    if (currentMode.value === EInvitationsMode.Received)
      return received
    return []
  }
  // #endregion

  // #region ACTIONS
  /**
   * Helper method to set the invitations mode (received/sent)
   */
  const setCurrentMode = (mode: EInvitationsMode) => {
    currentMode.value = mode
  }

  /**
   * Clear the store
   */
  const clear = () => {
    received.length = 0
  }

  /**
   * Mark the status of received invitations as read
   */
  const markAsRead = () => {
    showInvitesBadge.value = false
  }

  /**
   * Fetches invites based on the current mode
   *
   * @returns Data that is expected by Content stores
   */
  const fetchNext = async () => {
    if (currentMode.value === EInvitationsMode.Received) {
      const invites = await getInvitations(currentUser?.hash_key as string)
      // Set the status of fetched as true
      fetchedInvites.value = true

      for (const invite of invites) {
        // Avoid duplicates
        if (!received.some(item => item.id === invite.id))
          received.push(invite)
      }
    }

    // Always stop the infinite load
    return {
      data: {
        length: 0,
      },
    }
  }

  /**
   * Accepts the invitation from a user
   *
   * @param invitation The invitation item
   * @returns The status from the backend. Was the user accepted only or accepted and added to network
   */
  const acceptInvitation = async (invitation: ReceivedInvitation) => {
    let status = EInvitationAcceptStatus.AcceptAndAdd

    // If user is already in network, just accept
    if (threads.has(invitation.sender.login))
      status = EInvitationAcceptStatus.Accept

    try {
      await accept({
        id: invitation.id,
        status,
      })

      // Add user to network after accepting the invite
      const user = invitation.sender
      if (user.hash_key)
        addNewThread(user.hash_key, user)

      invitation.isAccepted = true
      return status
    }
    catch (err) {
      if (err
        && (err as SwymHTTPError).errorcode === 'IllegalArgumentException'
        && (err as SwymHTTPError).errormsg?.includes('already been answered')
      ) {
        const invitationIndex = received.findIndex(invite => invite.id === invitation.id)
        received.splice(invitationIndex, 1)
      }

      throw err
    }
  }

  /**
   * Declines an invite
   *
   * @param invitation The invitation item
   */
  const declineInvitation = async (invitation: ReceivedInvitation) => {
    try {
      await decline({
        id: invitation.id,
      })
      const invitationIndex = received.findIndex(invite => invite.id === invitation.id)
      received.splice(invitationIndex, 1)
    }
    catch (err) {
      if (err
        && (err as SwymHTTPError).errorcode === 'IllegalArgumentException'
        && (err as SwymHTTPError).errormsg?.includes('already been answered')
      ) {
        const invitationIndex = received.findIndex(invite => invite.id === invitation.id)
        received.splice(invitationIndex, 1)
      }

      throw err
    }
  }
  // #endregion

  return {
    // State
    limit: Number.MAX_SAFE_INTEGER,
    currentContents: contents,
    hasInvites,
    countInvites,
    showInvitesBadge: readonly(showInvitesBadge),
    fetchedInvites: readonly(fetchedInvites),

    // Getters
    getContents,

    // Actions
    markAsRead,
    setCurrentMode,
    doFetchNext: fetchNext,
    doClear: clear,
    acceptInvitation,
    declineInvitation,
  }
})
